import { Ship, Menu, X, Wifi, WifiOff, Settings, HelpCircle, LogOut, Download } from 'lucide-react';
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  isOnline: boolean;
  onSignOut?: () => void;
  onHelpCenter?: () => void;
  onSettings?: () => void;
}

export default function Layout({ children, isOnline, onSignOut, onHelpCenter, onSettings }: LayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuMsg, setMenuMsg] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-navy-950 flex flex-col">
      <header className="sticky top-0 z-40 glass-strong border-b border-navy-700/50">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-ocean-600 flex items-center justify-center shadow-lg shadow-ocean-900/40">
              <Ship className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-navy-100 text-sm leading-tight">Draft Survey</h1>
              <p className="text-[10px] text-navy-400 leading-tight tracking-wider uppercase">Professional</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${isOnline ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
              {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {isOnline ? 'Online' : 'Offline'}
            </div>
            <button type="button" onClick={() => setMenuOpen(!menuOpen)} className="p-2 hover:bg-navy-700/50 rounded-xl transition-colors">
              {menuOpen ? <X className="w-5 h-5 text-navy-300" /> : <Menu className="w-5 h-5 text-navy-300" />}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-navy-950/90 backdrop-blur-sm" onClick={() => setMenuOpen(false)}>
          <div className="absolute top-14 right-0 w-64 bg-navy-800 border-l border-navy-700 h-[calc(100vh-3.5rem)] p-4 space-y-2" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => { setMenuOpen(false); setTimeout(() => window.dispatchEvent(new Event('beforeinstallprompt')), 100); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors" style={{ color: 'var(--color-app-text)' }}>
              <Download className="w-4 h-4 text-ocean-400" /> Install App
            </button>
            <button type="button" onClick={() => { if (onSettings) { onSettings(); setMenuOpen(false); } else { setMenuMsg('Settings coming soon'); setMenuOpen(false); } }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-navy-700/50 text-navy-300 transition-colors text-sm">
              <Settings className="w-4 h-4" /> Settings
            </button>
            <button type="button" onClick={() => { if (onHelpCenter) { onHelpCenter(); setMenuOpen(false); } else { setMenuMsg('Help Center coming soon'); setMenuOpen(false); } }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-navy-700/50 text-navy-300 transition-colors text-sm">
              <HelpCircle className="w-4 h-4" /> Help Center
            </button>
            <div className="border-t border-navy-700 my-2" />
            {onSignOut && (
              <button type="button" onClick={onSignOut} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-rose-500/10 text-rose-400 transition-colors text-sm">
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            )}
          </div>
        </div>
      )}

      {menuMsg && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div className="bg-ocean-500/10 border border-ocean-500/20 rounded-xl px-4 py-3 flex items-center gap-2 text-ocean-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {menuMsg}
            <button type="button" onClick={() => setMenuMsg(null)} className="ml-auto text-navy-400 hover:text-navy-200">×</button>
          </div>
        </div>
      )}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6 sm:py-8 safe-bottom">
        {children}
      </main>

      <footer className="border-t border-navy-800/50 py-4 text-center text-xs text-navy-500">
        Draft Survey Pro v1.0 • Offline Ready • COMANAV Agent Tool
      </footer>
    </div>
  );
}
